/**
 * 統一されたロギングシステム
 * 開発環境と本番環境で異なる動作を提供
 * エラー追跡とパフォーマンス監視機能を含む
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4,
}

interface LogConfig {
  level: LogLevel;
  enableTimestamp: boolean;
  enableStackTrace: boolean;
  enablePerformanceMarks: boolean;
  prefix?: string;
  enableColors?: boolean;
  enableErrorTracking?: boolean;
  maxErrorHistory?: number;
}

interface ErrorEntry {
  timestamp: number;
  message: string;
  error?: Error;
  stack?: string;
  context?: object;
}

interface PerformanceEntry {
  label: string;
  duration: number;
  timestamp: number;
}

// カラーコード定義
const colors = {
  debug: '\x1b[36m', // Cyan
  info: '\x1b[32m',  // Green
  warn: '\x1b[33m',  // Yellow
  error: '\x1b[31m', // Red
  reset: '\x1b[0m',
};

class Logger {
  private config: LogConfig;
  private isProduction: boolean;
  private errorHistory: ErrorEntry[] = [];
  private performanceHistory: PerformanceEntry[] = [];
  private errorListeners: ((error: ErrorEntry) => void)[] = [];

  constructor(config?: Partial<LogConfig>) {
    this.isProduction = process.env.NODE_ENV === 'production';
    this.config = {
      level: this.isProduction ? LogLevel.WARN : LogLevel.DEBUG,
      enableTimestamp: true,
      enableStackTrace: !this.isProduction,
      enablePerformanceMarks: !this.isProduction,
      enableColors: !this.isProduction,
      enableErrorTracking: true,
      maxErrorHistory: 100,
      ...config,
    };
  }

  private formatMessage(level: string, message: string): string {
    const parts: string[] = [];
    
    if (this.config.enableTimestamp) {
      parts.push(`[${new Date().toISOString()}]`);
    }
    
    if (this.config.prefix) {
      parts.push(`[${this.config.prefix}]`);
    }
    
    parts.push(`[${level}]`);
    parts.push(message);
    
    return parts.join(' ');
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.config.level;
  }

  private getColor(level: string): string {
    if (!this.config.enableColors) return '';
    
    switch (level.toLowerCase()) {
      case 'debug': return colors.debug;
      case 'info': return colors.info;
      case 'warn': return colors.warn;
      case 'error': return colors.error;
      default: return '';
    }
  }

  debug(message: string, ...args: unknown[]): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return;
    
    const color = this.getColor('debug');
    const reset = this.config.enableColors ? colors.reset : '';
    console.debug(color + this.formatMessage('DEBUG', message) + reset, ...args);
  }

  info(message: string, ...args: unknown[]): void {
    if (!this.shouldLog(LogLevel.INFO)) return;
    
    const color = this.getColor('info');
    const reset = this.config.enableColors ? colors.reset : '';
    console.info(color + this.formatMessage('INFO', message) + reset, ...args);
  }

  warn(message: string, ...args: unknown[]): void {
    if (!this.shouldLog(LogLevel.WARN)) return;
    
    const color = this.getColor('warn');
    const reset = this.config.enableColors ? colors.reset : '';
    console.warn(color + this.formatMessage('WARN', message) + reset, ...args);
  }

  error(message: string, error?: Error | unknown, context?: object, ...args: unknown[]): void {
    if (!this.shouldLog(LogLevel.ERROR)) return;
    
    const color = this.getColor('error');
    const reset = this.config.enableColors ? colors.reset : '';
    console.error(color + this.formatMessage('ERROR', message) + reset, error, ...args);
    
    // Error tracking
    if (this.config.enableErrorTracking) {
      const errorEntry: ErrorEntry = {
        timestamp: Date.now(),
        message,
        error: error instanceof Error ? error : undefined,
        stack: error instanceof Error ? error.stack : undefined,
        context
      };
      
      this.addErrorToHistory(errorEntry);
      this.notifyErrorListeners(errorEntry);
    }
    
    if (this.config.enableStackTrace && error instanceof Error) {
      console.error('Stack trace:', error.stack);
    }
  }

  performance(label: string, fn: () => void): void {
    if (!this.config.enablePerformanceMarks) {
      fn();
      return;
    }

    const startMark = `${label}-start`;
    const endMark = `${label}-end`;
    const measureName = `${label}-duration`;

    performance.mark(startMark);
    const start = performance.now();
    fn();
    const duration = performance.now() - start;
    performance.mark(endMark);
    performance.measure(measureName, startMark, endMark);

    const measure = performance.getEntriesByName(measureName)[0];
    this.debug(`Performance [${label}]: ${measure.duration.toFixed(2)}ms`);
    
    // Track performance
    this.addPerformanceToHistory({
      label,
      duration,
      timestamp: Date.now()
    });
    
    // クリーンアップ
    performance.clearMarks(startMark);
    performance.clearMarks(endMark);
    performance.clearMeasures(measureName);
  }

  async performanceAsync<T>(label: string, fn: () => Promise<T>): Promise<T> {
    if (!this.config.enablePerformanceMarks) {
      return fn();
    }

    const start = performance.now();
    try {
      const result = await fn();
      const duration = performance.now() - start;
      this.debug(`Performance [${label}]: ${duration.toFixed(2)}ms`);
      return result;
    } catch (error) {
      const duration = performance.now() - start;
      this.error(`Performance [${label}] failed after ${duration.toFixed(2)}ms`, error);
      throw error;
    }
  }

  group(label: string, fn: () => void): void {
    if (!this.shouldLog(LogLevel.DEBUG)) {
      fn();
      return;
    }

    console.group(label);
    fn();
    console.groupEnd();
  }

  table(data: unknown, columns?: string[]): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return;
    console.table(data, columns);
  }

  setLevel(level: LogLevel): void {
    this.config.level = level;
  }

  getLevel(): LogLevel {
    return this.config.level;
  }

  // Error tracking methods
  private addErrorToHistory(errorEntry: ErrorEntry): void {
    this.errorHistory.push(errorEntry);
    
    if (this.errorHistory.length > (this.config.maxErrorHistory || 100)) {
      this.errorHistory.shift();
    }
  }

  private addPerformanceToHistory(perfEntry: PerformanceEntry): void {
    this.performanceHistory.push(perfEntry);
    
    // Keep only last 200 performance entries
    if (this.performanceHistory.length > 200) {
      this.performanceHistory.shift();
    }
  }

  private notifyErrorListeners(errorEntry: ErrorEntry): void {
    this.errorListeners.forEach(listener => {
      try {
        listener(errorEntry);
      } catch (err) {
        console.error('Error in error listener:', err);
      }
    });
  }

  public addErrorListener(listener: (error: ErrorEntry) => void): void {
    this.errorListeners.push(listener);
  }

  public removeErrorListener(listener: (error: ErrorEntry) => void): void {
    const index = this.errorListeners.indexOf(listener);
    if (index !== -1) {
      this.errorListeners.splice(index, 1);
    }
  }

  public getErrorHistory(): ErrorEntry[] {
    return [...this.errorHistory];
  }

  public getPerformanceHistory(): PerformanceEntry[] {
    return [...this.performanceHistory];
  }

  public clearHistory(): void {
    this.errorHistory = [];
    this.performanceHistory = [];
  }

  public generateCrashReport(): string {
    const report = {
      timestamp: new Date().toISOString(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown',
      url: typeof window !== 'undefined' ? window.location.href : 'Unknown',
      errors: this.errorHistory.slice(-10), // Last 10 errors
      performance: this.performanceHistory.slice(-20), // Last 20 performance entries
      config: this.config
    };
    
    return JSON.stringify(report, null, 2);
  }

  public exportLogs(): string {
    const logs = {
      errors: this.errorHistory,
      performance: this.performanceHistory,
      metadata: {
        exported: new Date().toISOString(),
        version: '1.0.0',
        config: this.config
      }
    };
    
    return JSON.stringify(logs, null, 2);
  }
}

// デフォルトロガーインスタンス
export const logger = new Logger();

// WebGL専用ロガー
export const webglLogger = new Logger({
  prefix: 'WebGL',
  enablePerformanceMarks: true,
  enableColors: true,
});

// パフォーマンス専用ロガー
export const perfLogger = new Logger({
  prefix: 'Performance',
  level: LogLevel.INFO,
  enablePerformanceMarks: true,
  enableColors: true,
});

// アプリケーションロガー
export const appLogger = new Logger({
  prefix: 'App',
  enableColors: true,
});

// ネットワーク専用ロガー
export const networkLogger = new Logger({
  prefix: 'Network',
  level: LogLevel.INFO,
  enableColors: true,
});

// 開発用ヘルパー関数
export const logGroup = (label: string, fn: () => void) => logger.group(label, fn);
export const logPerf = (label: string, fn: () => void) => logger.performance(label, fn);
export const logPerfAsync = async <T>(label: string, fn: () => Promise<T>) => 
  logger.performanceAsync(label, fn);

// グローバルに公開（開発環境のみ）
if (process.env.NODE_ENV === 'development') {
  (window as unknown as Record<string, unknown>).__logger = logger;
  (window as unknown as Record<string, unknown>).__webglLogger = webglLogger;
  (window as unknown as Record<string, unknown>).__perfLogger = perfLogger;
  (window as unknown as Record<string, unknown>).__LogLevel = LogLevel;
}